import { useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import { CheckCircle, Clock, XCircle, Info } from '@phosphor-icons/react';
import {
  AccentButton,
  NeutralButton,
  PrimaryButton,
} from '../../../../../shared/Buttons';
import { formatNumberCommasWithDollar } from '../../../../../utils/CurrencyUtils';
import {
  CashAdvanceOptionsStatus,
  DisplayOption,
  PresentedLoanOption,
} from './types/bankingTypes';
import {
  useCashAdvanceOptionsQuery,
  useDeclineCashAdvanceOptions,
  useSelectCashAdvanceOption,
} from './hooks/useCashAdvanceOptions';
import {
  formatDeadline,
  formatOptionForDisplay,
} from './hooks/cashAdvanceOptionsHelpers';

// Shared monthly-payment / total-cost grid, rendered both inside a selectable
// OptionCard and in the read-only "Plan selected" summary.
function OptionMoneyGrid({ display }: { display: DisplayOption }) {
  return (
    <div className="grid w-full grid-cols-2 gap-2">
      <p className="text-[14px] text-graysecondary">Monthly payment</p>
      <p className="text-right text-[16px] font-[600] text-primary">
        {formatNumberCommasWithDollar(display.monthly_payment_dollars)}
      </p>
      <p className="text-[14px] text-graysecondary">Total cost</p>
      <p className="text-right text-[16px] font-[600] text-primary">
        {formatNumberCommasWithDollar(display.total_cost_dollars)}
      </p>
    </div>
  );
}

function BackToAdvancesButton({
  onClick,
  addClass = 'rounded-full h-[48px] px-6 text-[14px]',
}: {
  onClick: () => void;
  addClass?: string;
}) {
  return (
    <NeutralButton onClick={onClick} addClass={addClass}>
      Back to advances
    </NeutralButton>
  );
}

function OptionCard({
  option,
  selected,
  disabled,
  onSelect,
}: {
  option: PresentedLoanOption;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  const display = formatOptionForDisplay(option);
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onSelect}
      className={`flex w-full flex-col items-start gap-3 rounded-[8px] border-[2px] p-4 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
        selected
          ? 'border-accent bg-bgyellow'
          : 'border-bordergray bg-white hover:border-accent'
      }`}
    >
      <div className="flex w-full items-center justify-between">
        <p className="text-[18px] font-[600] text-primary">
          {display.term_in_months} months
        </p>
        <span
          className={`flex h-5 w-5 items-center justify-center rounded-full border ${
            selected ? 'border-accent bg-accent' : 'border-graysecondary'
          }`}
        >
          {selected && (
            <span className="block h-2.5 w-2.5 rounded-full bg-white" />
          )}
        </span>
      </div>
      <OptionMoneyGrid display={display} />
    </button>
  );
}

function DeclineConfirmDialog({
  loading,
  onCancel,
  onConfirm,
}: {
  loading: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex w-full max-w-[420px] flex-col gap-4 rounded-[8px] bg-white p-6">
        <h2 className="text-[20px] font-[600] text-primary">
          Decline these options?
        </h2>
        <p className="text-[14px] text-graysecondary">
          None of these plans will be offered after you decline. You can request
          a new cash advance or contact support.
        </p>
        <label className="text-[14px] text-primary">
          Reason (optional)
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Tell us why these plans don't work for you"
            className="mt-1 w-full rounded-[4px] border border-bordergray p-2 text-[14px] focus:border-accent focus:outline-none"
            rows={3}
          />
        </label>
        <div className="flex justify-end gap-2">
          <NeutralButton
            onClick={onCancel}
            addClass="rounded-full h-[40px] px-4 text-[14px]"
          >
            Cancel
          </NeutralButton>
          <PrimaryButton
            onClick={() => onConfirm(reason)}
            disabled={loading}
            addClass="rounded-full h-[40px] px-4 text-[14px] flex items-center gap-2"
          >
            <span>Decline options</span>
            {loading && <ClipLoader color="#FFFFFF" loading size={14} />}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

function CashAdvanceOptions() {
  const { request_id } = useParams<{ request_id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/demo')
    ? '/demo'
    : '/dashboard';
  const goToAdvances = () =>
    navigate(`${basePath}/banking/cash-advances/requested`);

  const [selectedTermsId, setSelectedTermsId] = useState<string | null>(null);
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);

  const { data, isLoading, isError } = useCashAdvanceOptionsQuery(request_id);

  const selectMutation = useSelectCashAdvanceOption(request_id, () =>
    navigate(`${basePath}/banking/cash-advance/consent/${request_id}`)
  );
  const declineMutation = useDeclineCashAdvanceOptions(request_id);

  const status = data?.status ?? CashAdvanceOptionsStatus.None;

  const sortedOptions = useMemo(
    () =>
      [...(data?.options ?? [])].sort(
        (a, b) => (a.term_in_months ?? 0) - (b.term_in_months ?? 0)
      ),
    [data?.options]
  );

  if (isLoading) {
    return (
      <ClipLoader
        color="#1D213B"
        loading
        size={60}
        aria-label="Loading Spinner"
        data-testid="loader"
        className="mt-10 flex h-full w-full justify-center"
      />
    );
  }

  if (isError || !data) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-2 pt-[7rem]">
        <p className="text-[16px] text-primary">
          We couldn't load your repayment options.
        </p>
        <BackToAdvancesButton
          onClick={goToAdvances}
          addClass="rounded-full h-[40px] px-4 text-[14px]"
        />
      </div>
    );
  }

  const renderActive = () => (
    <>
      <div className="flex w-full flex-col gap-2">
        <h1 className="text-[28px] font-[600] text-primary">
          Choose your repayment plan
        </h1>
        <p className="text-[14px] text-graysecondary">
          Pick the option that works best for your business, then sign your
          agreement to receive your funds.
        </p>
      </div>
      <div className="flex w-full items-center gap-2 rounded-[8px] border border-[#F7A826] bg-bgyellow p-3">
        <Clock size={20} className="text-primary" />
        <p className="text-[14px] text-primary">
          Choose by {formatDeadline(data.options_expire_at)}.
        </p>
      </div>
      <div className="flex w-full flex-col gap-3">
        {sortedOptions.map((option) => (
          <OptionCard
            key={option.terms_id}
            option={option}
            selected={selectedTermsId === option.terms_id}
            disabled={selectMutation.isPending || declineMutation.isPending}
            onSelect={() => setSelectedTermsId(option.terms_id)}
          />
        ))}
        {sortedOptions.length === 0 && (
          <p className="text-[14px] text-graysecondary">
            No repayment options to review right now.
          </p>
        )}
      </div>
      <div className="flex w-full flex-col gap-2 md:flex-row md:justify-end">
        <NeutralButton
          onClick={() => setShowDeclineDialog(true)}
          disabled={selectMutation.isPending || declineMutation.isPending}
          addClass="rounded-full h-[48px] px-6 text-[14px]"
        >
          None of these work
        </NeutralButton>
        <AccentButton
          onClick={() =>
            selectedTermsId && selectMutation.mutate(selectedTermsId)
          }
          disabled={
            !selectedTermsId ||
            selectMutation.isPending ||
            declineMutation.isPending
          }
          addClass="rounded-full h-[48px] px-6 text-[14px] flex items-center gap-2"
        >
          <span>Confirm</span>
          {selectMutation.isPending && (
            <ClipLoader color="#1D213B" loading size={16} />
          )}
        </AccentButton>
      </div>
      {showDeclineDialog && (
        <DeclineConfirmDialog
          loading={declineMutation.isPending}
          onCancel={() => setShowDeclineDialog(false)}
          onConfirm={(reason) =>
            declineMutation.mutate(reason, {
              onSettled: () => setShowDeclineDialog(false),
            })
          }
        />
      )}
    </>
  );

  const renderSelected = () => {
    const selectedOption = sortedOptions.find((o) => o.selected) ?? null;
    const display = selectedOption
      ? formatOptionForDisplay(selectedOption)
      : null;
    return (
      <>
        <div className="flex w-full flex-col items-start gap-2">
          <div className="flex items-center gap-2">
            <CheckCircle size={28} weight="fill" className="text-green-600" />
            <h1 className="text-[24px] font-[600] text-primary">
              Plan selected
            </h1>
          </div>
          <p className="text-[14px] text-graysecondary">
            Sign your cash advance agreement to complete the process and receive
            your funds.
          </p>
        </div>
        {display && (
          <div className="flex w-full flex-col gap-3 rounded-[8px] border border-bordergray bg-white p-4">
            <p className="text-[18px] font-[600] text-primary">
              {display.term_in_months} months
            </p>
            <OptionMoneyGrid display={display} />
          </div>
        )}
        <div className="flex w-full justify-end">
          <AccentButton
            onClick={() =>
              navigate(`${basePath}/banking/cash-advance/consent/${request_id}`)
            }
            addClass="rounded-full h-[48px] px-6 text-[14px]"
          >
            Continue to sign agreement
          </AccentButton>
        </div>
      </>
    );
  };

  const renderDeclined = () => (
    <>
      <div className="flex w-full flex-col items-start gap-2">
        <div className="flex items-center gap-2">
          <XCircle size={28} weight="fill" className="text-graysecondary" />
          <h1 className="text-[24px] font-[600] text-primary">
            Options declined
          </h1>
        </div>
        <p className="text-[14px] text-graysecondary">
          You declined these repayment options
          {data.options_declined_at
            ? ` on ${formatDeadline(data.options_declined_at)}`
            : ''}
          .
        </p>
        {data.options_declined_reason && (
          <div className="w-full rounded-[8px] border border-bordergray bg-white p-3">
            <p className="text-[14px] text-graysecondary">Reason</p>
            <p className="text-[14px] text-primary">
              {data.options_declined_reason}
            </p>
          </div>
        )}
        <p className="text-[14px] text-graysecondary">
          Need a new plan? Request a new cash advance or contact support.
        </p>
      </div>
      <div className="flex w-full justify-end">
        <BackToAdvancesButton onClick={goToAdvances} />
      </div>
    </>
  );

  const renderExpired = () => (
    <>
      <div className="flex w-full flex-col items-start gap-2">
        <div className="flex items-center gap-2">
          <Info size={28} weight="fill" className="text-[#C64949]" />
          <h1 className="text-[24px] font-[600] text-primary">
            Options expired
          </h1>
        </div>
        <p className="text-[14px] text-graysecondary">
          These options expired
          {data.options_expired_at
            ? ` on ${formatDeadline(data.options_expired_at)}`
            : data.options_expire_at
              ? ` on ${formatDeadline(data.options_expire_at)}`
              : ''}
          . To get new repayment options, request a new cash advance or contact
          support.
        </p>
      </div>
      <div className="flex w-full justify-end">
        <BackToAdvancesButton onClick={goToAdvances} />
      </div>
    </>
  );

  const renderNone = () => (
    <div className="flex w-full flex-col items-center gap-2 pt-[2rem]">
      <p className="text-[16px] text-primary">
        No repayment options are available for this request yet.
      </p>
      <BackToAdvancesButton
        onClick={goToAdvances}
        addClass="rounded-full h-[40px] px-4 text-[14px]"
      />
    </div>
  );

  const renderStatusBranch = () => {
    switch (status) {
      case CashAdvanceOptionsStatus.Active:
        return renderActive();
      case CashAdvanceOptionsStatus.Selected:
        return renderSelected();
      case CashAdvanceOptionsStatus.Declined:
        return renderDeclined();
      case CashAdvanceOptionsStatus.Expired:
        return renderExpired();
      default:
        return renderNone();
    }
  };

  return (
    <div className="flex h-full w-full flex-col items-center overflow-y-auto pt-[2rem]">
      <div className="flex w-full max-w-[640px] flex-col gap-4 px-4 pb-16">
        {renderStatusBranch()}
      </div>
    </div>
  );
}

export default CashAdvanceOptions;
